import { type ReactNode } from 'react'
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Label } from '~/components/ui/label'

type ControlledFormItemProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  control: Control<TFieldValues>
  name: TName
  label?: string
  required?: boolean
  render: (field: {
    value: TFieldValues[TName]
    onChange: (value: TFieldValues[TName]) => void
    onBlur: () => void
    name: TName
    ref: React.Ref<HTMLElement>
  }) => ReactNode
}

export function ControlledFormItem<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  required,
  render,
}: ControlledFormItemProps<TFieldValues, TName>) {
  const { t } = useTranslation()

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <div className="space-y-2">
          {label && (
            <Label htmlFor={String(name)}>
              {label}
              {required && <span className="ml-1 text-destructive">*</span>}
            </Label>
          )}
          {render(field)}
          {error?.message && <p className="text-sm text-destructive">{t(`error.${error.message}`)}</p>}
        </div>
      )}
    />
  )
}
